import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import commentApi from "../../api/comment";
import documentApi from "../../api/documentApi";
import { formatBytes } from "../../common";
import { CommentItem } from "../../common/type";
import ButtonIcon from "../../components/ButtonIcon";
import Comment from "../../components/Comment";
import CommentBox from "../../components/CommentBox";
import CommentTag from "../../components/CommentTag";
import File from "../../components/File";
import Layout from "../../components/Layout";
import Title from "../../components/Title";
import UploadPopup from "../../components/UploadPopup";
import WarningDeletePopup from "../../components/WarningDeletePopup";
import { useGetPrivateDocument } from "../../hooks/query/useGetPrivateDocument";
import { useGetAllSubject } from "../../hooks/query/useGetAllSubject"; // 👈
import { RootState } from "../../store";

interface CommentResponse {
    items: CommentItem[];
    meta: {
        currentPage: number;
        totalPages: number;
    };
}

const LIMIT = 10;
const PRIVATE_TAB_ID = -1; // 👈 dùng id đặc biệt tránh trùng id từ API

function Document() {
    const { user } = useSelector((state: RootState) => state.auth);
    const isStaff = user?.roles.includes("student_management") || user?.roles.includes("teacher") || user?.roles.includes("branch_manager") || user?.roles.includes("staff") || user?.roles.includes("admin");

    const { data: subjectsData = [] } = useGetAllSubject(); // 👈

    const tabs = [
        ...subjectsData.map((subject: any) => ({ id: subject.id, name: subject.name })),
        ...(isStaff ? [{ id: PRIVATE_TAB_ID, name: "Tài liệu riêng tư" }] : []),
    ]; // 👈 build tabs từ API

    const [activeTab, setActiveTab] = useState<number | null>(null);
    const activeTabId = activeTab ?? subjectsData[0]?.id ?? null; // 👈 mặc định tab đầu tiên

    const [searchName, setSearchName] = useState<string>("");
    const [gradeLevel, setGradeLevel] = useState<number>(0);
    const [isPrivate, setPrivate] = useState<boolean>(false);
    const [show, setShow] = useState<boolean>(false);
    const [fileToDelete, setFileToDelete] = useState<number | null>(null);
    const [resetCommentBox, setResetCommentBox] = useState<boolean>(false);
    const [showPopup, setShowPopup] = useState(false);

    const queryClient = useQueryClient();

    const handleChangeTab = (id: number) => {
        setActiveTab(id);
        setPrivate(id === PRIVATE_TAB_ID); // 👈
    };

    const { data: files = [], refetch } = useQuery({
        queryKey: ["documents", activeTabId, gradeLevel, searchName],
        queryFn: async () => {
            if (activeTabId === PRIVATE_TAB_ID || !activeTabId) return [];

            const res = await documentApi.getDocumentBySubject({
                subjectId: activeTabId,
                ...(gradeLevel > 0 && { gradeLevel }),
                ...(searchName && { name: searchName }),
            });

            return res.data
                .sort(
                    (a: any, b: any) =>
                        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                )
                .map((item: any) => ({
                    id: item?.id,
                    title: item?.name,
                    type: item?.type,
                    size: formatBytes(item?.size),
                    fileKey: item?.fileKey,
                }));
        },
        enabled: !!activeTabId && activeTabId !== PRIVATE_TAB_ID, // 👈 không fetch khi tab riêng tư
        retry: 0,
    });

    const handleUpload = () => setShowPopup(true);
    const handleCloseDeletePopup = () => { setShow(false); setFileToDelete(null); };
    const handleCloseUploadPopup = () => setShowPopup(false);
    const uploadSuccess = () => { toast.success("Upload thành công!"); refetch(); };
    const handleDeleteFile = (id: number) => { setFileToDelete(id); setShow(true); };

    const handleSubmitComment = async (payload: { comment: string; file: File | null, parentCommentId?: number | null }) => {
        const formData = new FormData();
        formData.append("content", payload.comment);
        if (payload.parentCommentId !== null && payload.parentCommentId !== undefined) {
            formData.append("parentCommentId", payload.parentCommentId.toString());
        }
        if (payload.file) formData.append("file", payload.file);
        try {
            await commentApi.createComment(formData);
            queryClient.invalidateQueries({ queryKey: ["comments"] });
            setResetCommentBox(prev => !prev);
            toast.success("Bình luận thành công!");
        } catch (error) {
            console.error("Error creating comment:", error);
            toast.error("Có lỗi xảy ra khi gửi bình luận.");
        }
    };

    const { data: commentsQuery, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
        useInfiniteQuery<CommentResponse>({
            queryKey: ["comments"],
            initialPageParam: 1,
            queryFn: async ({ pageParam }) => {
                const res = await commentApi.getAllComments({ page: pageParam ?? 1, limit: LIMIT });
                return res.data;
            },
            getNextPageParam: (lastPage) => {
                if (!lastPage?.meta) return undefined;
                const { currentPage, totalPages } = lastPage.meta;
                return currentPage < totalPages ? currentPage + 1 : undefined;
            },
        });

    const comments = commentsQuery?.pages.flatMap(page => page?.items) ?? [];

    const { data: privateDocs = [] } = useGetPrivateDocument(user?.id, activeTabId === PRIVATE_TAB_ID);

    const privateFiles = privateDocs.map((item: any) => ({
        id: item.id,
        title: item.name,
        type: item.type,
        size: formatBytes(item.size),
        fileKey: item.fileKey,
    }));

    const displayFiles = activeTabId === PRIVATE_TAB_ID ? privateFiles : files;

    return (
        <Layout>
            {show && (
                <WarningDeletePopup
                    fileId={fileToDelete}
                    onClose={handleCloseDeletePopup}
                    onCancel={handleCloseDeletePopup}
                    onSuccess={() => { toast.success("Xóa file thành công!"); refetch(); }}
                />
            )}
            {showPopup && <UploadPopup onSuccess={uploadSuccess} onClose={handleCloseUploadPopup} isPrivate={isPrivate} />}
            <section className="w-full h-fit py-[60px] px-[20px] lg:px-[50px] text-center bg-[#f9f9f9]">
                <Title title="Tài Liệu Học Tập" subTitle="Tải về tài liệu và bài tập cho từng môn học" />
                <div className="mx-auto mt-[40px] w-fit flex flex-wrap justify-center items-center gap-[20px]">
                    {tabs.map((tab) => (
                        <span
                            key={tab.id}
                            onClick={() => handleChangeTab(tab.id)}
                            className={`py-[10px] px-[20px] rounded-[30px] cursor-pointer transition-all duration-300
                                ${activeTabId === tab.id
                                    ? "bg-secondary text-white shadow-md"
                                    : "bg-white text-bold border border-[#e0e0e0] hover:bg-secondary hover:text-white"
                                }`}
                        >
                            {tab.name}
                        </span>
                    ))}
                </div>

                {/* phần còn lại giữ nguyên */}
                <div className="mx-auto w-[44%] my-[40px] flex">
                    <div className="bg-white shadow-md flex-1 border border-[#e0e0e0] rounded-[8px] flex items-center ml-[20px] overflow-hidden">
                        <input type="text" className="w-full py-[8px] px-[12px] outline-none" placeholder="Nhập tên tài liệu để tìm..." value={searchName}
                            onChange={(e) => setSearchName(e.target.value)} />
                    </div>
                    <div className="ml-[20px] bg-white shadow-md border border-[#e0e0e0] rounded-[8px] overflow-hidden">
                        <select className="outline-none py-[8px] px-[12px]" value={gradeLevel ?? 0}
                            onChange={(e) => setGradeLevel(Number(e.target.value))}>
                            <option value={0}>Chọn lớp</option>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                    </div>
                </div>

                {isStaff && (
                    <div className="my-[20px]">
                        <ButtonIcon label="Upload tài liệu" onClick={handleUpload} styleButton="ml-auto" />
                    </div>
                )}

                <div className="mt-[40px] text-lg">
                    <div className={`bg-white p-[20px] rounded-[10px] shadow-default h-fit max-h-[440px] ${displayFiles.length > 0 ? "custom-scroll overflow-y-scroll" : ""}`}>
                        <div className="flex flex-col py-[20px]">
                            {displayFiles.length === 0 ? (
                                <p className="text-gray-500 text-center">
                                    {activeTabId === PRIVATE_TAB_ID ? "Bạn chưa có tài liệu riêng tư" : "Chưa có tài liệu cho môn học này"}
                                </p>
                            ) : (
                                displayFiles.map((file: any) => (
                                    <File key={file.id} type={file.type} title={file.title} size={file.size}
                                        fileKey={file.fileKey} id={file.id} onDelete={(id) => handleDeleteFile(id)} />
                                ))
                            )}
                        </div>
                    </div>

                    <div className="bg-white mt-[60px] p-[20px] h-fit shadow-md rounded-[10px]">
                        <CommentBox onComment={(data) => handleSubmitComment(data)} onReset={resetCommentBox} />
                        <div className="mt-[30px]">
                            {isLoading && <p>Đang tải bình luận...</p>}
                            {comments.map((comment) => (
                                <Comment key={comment.id} data={comment}
                                    onReply={(parentId, payload) => handleSubmitComment({ ...payload, parentCommentId: parentId })} />
                            ))}
                            {hasNextPage && (
                                <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                                    {isFetchingNextPage ? "Đang tải..." : "Load more"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-[60px]">
                    <Title title="Đánh Giá & Nhận Xét" subTitle="Theo dõi tiến độ học tập và nhận phản hồi từ giáo viên" />
                    <div className="flex flex-col lg:flex-row lg:justify-between flex-wrap mt-[30px]">
                        <CommentTag type="test" title="Bài kiểm tra Toán giữa kỳ" date="15/10/2023" subTitle="Đánh giá kiến thức chương 1-3 môn Toán lớp 5" score="8.5" />
                        <CommentTag type="homework" title="Bài tập Tiếng Việt tuần 4" date="15/10/2023" subTitle="Bài tập làm văn miêu tả con vật" status={true} comment="Bài viết tốt, cần chú ý chính tả nhiều hơn." />
                        <CommentTag type="test" title="Bài kiểm tra Toán giữa kỳ" date="15/10/2023" subTitle="Đánh giá kiến thức chương 1-3 môn Toán lớp 5" score="8.5" />
                        <CommentTag type="homework" title="Bài tập Tiếng Việt tuần 4" date="15/10/2023" subTitle="Bài tập làm văn miêu tả con vật" status={false} comment="Bài viết tốt, cần chú ý chính tả nhiều hơn." />
                    </div>
                </div>
            </section>
        </Layout>
    );
}

export default Document;